// The account order-history / order-detail pages' `#aux-order-history-header-
// content` region above the order list: the
// CheckoutSettings.aux_order_history_header_content Toolbox stream, dispatched
// through the shared buildToolboxPageMap so its blocks hydrate as real React
// children.
import { captureException } from "@sentry/browser";
import React from "react";

import { interfaces } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { buildToolboxPageMap } from "../../blocks/toolboxPageMap";

// Render-nothing error boundary scoped to the auxiliary region: a throw in the
// parse/dispatch below degrades only this header, never the whole order page.
class AuxContentBoundary extends React.PureComponent<
    { children: React.ReactNode },
    { failed: boolean }
> {
    public state = { failed: false };

    componentDidCatch(error: Error): void {
        captureException(error);
        this.setState({ failed: true });
    }

    render(): React.ReactNode {
        return this.state.failed ? null : this.props.children;
    }
}

const AuxOrderHistoryContentInner = (props: {
    payload: string;
}): React.JSX.Element | null => {
    if (!props.payload) {
        return null;
    }
    // The payload is TempurpedicDocumentHooks.aux_order_history_content's
    // reactivated-serialized OrderHistoryAuxContentInterface: the
    // CheckoutSettings.aux_order_history_header_content Toolbox stream plus the 7
    // get_toolbox_block_ssr side-channels. Dispatching it through the shared
    // toolbox page map keeps the blocks real React children of the document.
    const aux = JSON.parse(
        props.payload,
    ) as interfaces.OrderHistoryAuxContentInterface;
    const stream = aux.checkout_settings.aux_order_history_header_content;
    if (!stream) {
        return null;
    }
    const map = buildToolboxPageMap(aux);
    // Wagtail's StreamBlock.render_basic wraps every top-level block in
    // `<div class="w-block-{type} block-{type}">`. Reproduced per top-level
    // block via the per-block `StreamField tagName="div"` render.
    return (
        <>
            {stream.value.map((block) => (
                <StreamField
                    key={block.id}
                    tagName="div"
                    attrs={{
                        className: `w-block-${block.type} block-${block.type}`,
                    }}
                    value={[block]}
                    components={map}
                />
            ))}
        </>
    );
};

// Exported for direct unit coverage of the containment behavior.
export const AuxOrderHistoryContent = (props: {
    payload: string;
}): React.JSX.Element => (
    <AuxContentBoundary>
        <AuxOrderHistoryContentInner payload={props.payload} />
    </AuxContentBoundary>
);
